import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Tabs, Tab, Typography, CircularProgress, Alert, Tooltip } from "@mui/material";

import LoopThemeSelector from "./LoopThemeSelector";
import LoopKPISelector from "./LoopKPISelector";
import LoopGoalSetter from "./LoopGoalSetter";
import LoopLayerSelector from "./LoopLayerSelector";
import LoopSegmentSelector from "./LoopSegmentSelector";
import LoopReference from "./LoopReference";

import { 
    setCurrentLoop,
    setLoopThemes,
    setLoopKPIs,
    setLoopGoals,
    setLoopLayers,
    setLoopSegments,
    setLoopLoading,
    setLoopError,
    setLoopLength,
} from "../../../../store/slices/loopSlice.js";
import { Capitalize, Pluralize } from "../../../../functions/Formatters";
import { global } from "../../../../context/global";
import StepperButtons from "../../StepperButtons";

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`loop-tabpanel-${index}`}
            aria-labelledby={`loop-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

export default function LoopManagement({ loopId, editable = true, currentLoopIsLastLoop=false, payload }) {
    const dispatch = useDispatch();
    const { currentLoop, loading, error } = useSelector((state) => state.loop);
    const { selectedProject } = useSelector((state) => state.projects);
    
    const [tabValue, setTabValue] = useState(0);

    const derivedLoop = useMemo(() => {
        if (!selectedProject || !loopId) return null;
        for (const phase of selectedProject.phases || []) {
            const found = (phase.loops || []).find((l) => l.id === loopId);
            if (found) return found;
        }
        return null;
    }, [selectedProject, loopId]);

    useEffect(() => {
        // Populate loop slice from selectedProject, no API calls
        dispatch(setLoopLoading(true));
        dispatch(setLoopError(null));
        if (derivedLoop) {
            dispatch(setCurrentLoop({ id: derivedLoop.id }));
            dispatch(setLoopThemes(derivedLoop.loop_themes || []));
            dispatch(setLoopKPIs(derivedLoop.loop_kpis || []));
            // Flatten goals from loop_themes
            const flatGoals = (derivedLoop.loop_kpis || []).flatMap((lk) => lk.loop_goals || []);
            dispatch(setLoopGoals(flatGoals));
            dispatch(setLoopLayers(derivedLoop.loop_layers || []));
            dispatch(setLoopSegments(derivedLoop.loop_segments || []));
            dispatch(setLoopLength(derivedLoop.length));
            dispatch(setLoopLoading(false));
        } else {
            // If loop not found, clear and show error
            dispatch(setCurrentLoop(null));
            dispatch(setLoopThemes([]));
            dispatch(setLoopKPIs([]));
            dispatch(setLoopGoals([]));
            dispatch(setLoopLayers([]));
            dispatch(setLoopSegments([]));
            dispatch(setLoopLength(null));
            dispatch(setLoopLoading(false));
        }
    }, [derivedLoop, dispatch]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                Fout bij het laden van ontwerpronde gegevens: {error}
            </Alert>
        );
    }

    if (!currentLoop) {
        return (
            <Alert severity="info" sx={{ m: 2 }}>
                Geen ontwerpronde geselecteerd
            </Alert>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            {/* <Paper sx={{ width: '100%' }}> */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1 }}>
                        {`Beheer ${Pluralize(global.theme)}, KPI's, doelen, lagen en segmenten voor deze ontwerpronde`}
                    </Typography>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        aria-label="loop management tabs"
                        sx={{ px: 2 }}
                    >
                        <Tooltip title={`Selecteer en beheer de ${Pluralize(global.theme)} die relevant zijn voor deze ontwerpronde. ${Capitalize(Pluralize(global.theme))} bepalen welke aspecten van het ontwerp worden geëvalueerd.`} placement="top">
                            <Tab label={Capitalize(Pluralize(global.theme))} />
                        </Tooltip>
                        <Tooltip title="Configureer de KPI's (Key Performance Indicators) die gebruikt worden om de prestaties van verschillende ontwerpen te meten en vergelijken." placement="top">
                            <Tab label="KPI's" />
                        </Tooltip>
                        <Tooltip title="Kies welke onderdelen van dijkvakken worden meegenomen in de analyse. Lagen kunnen verschillende componenten of aspecten van het ontwerp vertegenwoordigen." placement="top">
                            <Tab label="Onderdelen" />
                        </Tooltip>
                        <Tooltip title="Definieer de dijkvakken (segmenten) die worden geanalyseerd. Elk dijkvak kan verschillende eigenschappen en vereisten hebben." placement="top">
                            <Tab label="Dijkvakken" />
                        </Tooltip>
                        <Tooltip title="Stel het referentieontwerp in dat als basis dient voor vergelijking met andere ontwerpen in deze ontwerpronde." placement="top">
                            <Tab label="Referentie" />
                        </Tooltip>
                        <Tooltip title="Stel doelen en targets in voor de geselecteerde KPI's. Deze doelen worden gebruikt om de prestaties van ontwerpen te evalueren." placement="top">
                            <Tab label="Doelen" />
                        </Tooltip>
                    </Tabs>
                </Box>

                <TabPanel value={tabValue} index={0}>
                    <LoopThemeSelector loopId={loopId} editable={editable} currentLoopIsLastLoop={currentLoopIsLastLoop} />
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    <LoopKPISelector loopId={loopId} editable={editable} />
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                    <LoopLayerSelector loopId={loopId} editable={editable} />
                </TabPanel>

                <TabPanel value={tabValue} index={3}>
                    <LoopSegmentSelector loopId={loopId} editable={editable} />
                </TabPanel>

                <TabPanel value={tabValue} index={4}>
                    <LoopReference loopId={loopId} editable={editable} />
                </TabPanel>

                <TabPanel value={tabValue} index={5}>
                    <LoopGoalSetter loopId={loopId} editable={editable} />
                </TabPanel>

                <StepperButtons
                    payload={payload}
                    loopManagementTabs={{ tabIndex: tabValue, tabCount: 6, setTabIndex: setTabValue }}
                />
            {/* </Paper> */}
        </Box>
    );
} 